/**
 * CaseTracker - Seguimiento de casos de denuncias
 * Features:
 * - Línea de tiempo del caso
 * - Agregar acciones/comentarios
 * - Cambiar estado
 * - Resolver caso
 */
import { useState, useEffect } from 'react';
import {
  Clock,
  User,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  Archive,
  Edit,
  Calendar,
} from 'lucide-react';
import { denunciasAPI } from '../../api';
import { Button, Loading, Alert } from '../common';
import useAuthStore from '../../store/authStore';

const CaseTracker = ({ denunciaId, onUpdate }) => {
  const { user } = useAuthStore();
  const isMiembroComite = user?.es_miembro_comite || user?.rol === 'superadmin' || user?.rol === 'admin_org';

  const [denuncia, setDenuncia] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Formularios
  const [showAddAction, setShowAddAction] = useState(false);
  const [actionForm, setActionForm] = useState({
    tipo: 'comentario',
    descripcion: '',
  });

  const [showResolve, setShowResolve] = useState(false);
  const [resolveForm, setResolveForm] = useState({
    resolucion: '',
    acciones_tomadas: '',
  });

  useEffect(() => {
    loadDenuncia();
    loadTimeline();
  }, [denunciaId]);

  const loadDenuncia = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await denunciasAPI.getById(denunciaId);
      setDenuncia(response.data);
    } catch (err) {
      console.error('Error al cargar denuncia:', err);
      setError(err.message || 'Error al cargar denuncia');
    } finally {
      setLoading(false);
    }
  };

  const loadTimeline = async () => {
    try {
      const response = await denunciasAPI.getTimeline(denunciaId);
      setTimeline(response.data);
    } catch (err) {
      console.error('Error al cargar timeline:', err);
    }
  };

  const handleAgregarAccion = async (e) => {
    e.preventDefault();

    if (!actionForm.descripcion.trim()) {
      setError('La descripción es requerida');
      return;
    }

    try {
      setError(null);
      await denunciasAPI.agregarAccion(denunciaId, actionForm);
      
      setSuccess('Acción agregada correctamente');
      setShowAddAction(false);
      setActionForm({ tipo: 'comentario', descripcion: '' });
      loadTimeline();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al agregar acción:', err);
      setError(err.message || 'Error al agregar acción');
    }
  };

  const handleCambiarEstado = async (nuevoEstado) => {
    try {
      setError(null);
      await denunciasAPI.cambiarEstado(denunciaId, { estado: nuevoEstado });
      
      setSuccess(`Estado cambiado a: ${nuevoEstado}`);
      loadDenuncia();
      loadTimeline();
      
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al cambiar estado:', err);
      setError(err.message || 'Error al cambiar estado');
    }
  };

  const handleResolver = async (e) => {
    e.preventDefault();

    if (!resolveForm.resolucion.trim()) {
      setError('La resolución es requerida');
      return;
    }

    try {
      setError(null);
      await denunciasAPI.resolver(denunciaId, resolveForm);
      
      setSuccess('Caso resuelto correctamente');
      setShowResolve(false);
      setResolveForm({ resolucion: '', acciones_tomadas: '' });
      loadDenuncia();
      loadTimeline();
      
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al resolver caso:', err);
      setError(err.message || 'Error al resolver caso');
    }
  };

  const getEstadoInfo = (estado) => {
    const estados = {
      nueva: { label: 'Nueva', color: 'blue', icon: AlertTriangle },
      en_investigacion: { label: 'En Investigación', color: 'yellow', icon: Clock },
      resuelta: { label: 'Resuelta', color: 'green', icon: CheckCircle },
      archivada: { label: 'Archivada', color: 'gray', icon: Archive },
    };
    return estados[estado] || estados.nueva;
  };

  const getActionIcon = (tipo) => {
    const icons = {
      comentario: MessageSquare,
      actualizacion: Edit,
      cambio_estado: CheckCircle,
      resolucion: CheckCircle,
    };
    return icons[tipo] || MessageSquare;
  };

  if (loading) {
    return (
      <div className="p-8">
        <Loading size="lg" text="Cargando caso..." />
      </div>
    );
  }

  if (!denuncia) {
    return <Alert type="error" title="Error" message="No se pudo cargar el caso" />;
  }

  const estadoInfo = getEstadoInfo(denuncia.estado);
  const EstadoIcon = estadoInfo.icon;

  return (
    <div className="space-y-6">
      {/* Alertas */}
      {error && (
        <Alert type="error" title="Error" message={error} onClose={() => setError(null)} />
      )}

      {success && (
        <Alert
          type="success"
          title="Éxito"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Header del caso */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-primary-900">
              Denuncia #{denuncia.id_denuncia}
            </h3>
            <p className="text-primary-700 mt-1">{denuncia.tipo_denuncia}</p>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium bg-${estadoInfo.color}-100 text-${estadoInfo.color}-800`}
          >
            <EstadoIcon className="inline h-4 w-4 mr-1" />
            {estadoInfo.label}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2 text-primary-800">
            <Calendar className="h-4 w-4" />
            <span>
              Creada: {new Date(denuncia.fecha_creacion).toLocaleDateString('es-ES')}
            </span>
          </div>
          {denuncia.investigador && (
            <div className="flex items-center gap-2 text-primary-800">
              <User className="h-4 w-4" />
              <span>Investigador: {denuncia.investigador.nombre_completo}</span>
            </div>
          )}
        </div>
      </div>

      {/* Descripción */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-2">Descripción</h4>
        <p className="text-gray-700 whitespace-pre-line">{denuncia.descripcion}</p>
        
        {denuncia.anonima && (
          <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded-r">
            <p className="text-sm text-yellow-900">
              <strong>Denuncia Anónima:</strong> La identidad del denunciante está protegida.
            </p>
          </div>
        )}
      </div>

      {/* Acciones disponibles (solo para miembros del comité) */}
      {isMiembroComite && denuncia.estado !== 'resuelta' && denuncia.estado !== 'archivada' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Acciones</h4>
          <div className="flex flex-wrap gap-3">
            {denuncia.estado === 'nueva' && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleCambiarEstado('en_investigacion')}
              >
                Iniciar Investigación
              </Button>
            )}

            {denuncia.estado === 'en_investigacion' && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowResolve(true)}
              >
                Resolver Caso
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddAction(!showAddAction)}
            >
              Agregar Comentario
            </Button>

            {denuncia.estado !== 'archivada' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCambiarEstado('archivada')}
              >
                Archivar
              </Button>
            )}
          </div>

          {/* Formulario de agregar acción */}
          {showAddAction && (
            <form onSubmit={handleAgregarAccion} className="mt-4 space-y-3">
              <textarea
                value={actionForm.descripcion}
                onChange={(e) =>
                  setActionForm({ ...actionForm, descripcion: e.target.value })
                }
                rows={3}
                placeholder="Escribe tu comentario o actualización..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <div className="flex gap-2">
                <Button type="submit" variant="primary" size="sm">
                  Agregar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddAction(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          )}

          {/* Formulario de resolución */}
          {showResolve && (
            <form onSubmit={handleResolver} className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resolución *
                </label>
                <textarea
                  value={resolveForm.resolucion}
                  onChange={(e) =>
                    setResolveForm({ ...resolveForm, resolucion: e.target.value })
                  }
                  rows={3}
                  placeholder="Describe la resolución del caso..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Acciones Tomadas
                </label>
                <textarea
                  value={resolveForm.acciones_tomadas}
                  onChange={(e) =>
                    setResolveForm({ ...resolveForm, acciones_tomadas: e.target.value })
                  }
                  rows={3}
                  placeholder="Describe las acciones tomadas (opcional)..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" variant="primary" size="sm">
                  Resolver Caso
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResolve(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Línea de Tiempo</h4>
        
        {timeline.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No hay actividad registrada</p>
        ) : (
          <div className="space-y-4">
            {timeline.map((item, index) => {
              const ActionIcon = getActionIcon(item.tipo);
              
              return (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <ActionIcon className="h-5 w-5 text-primary-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {item.usuario?.nombre_completo || 'Sistema'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(item.fecha).toLocaleString('es-ES')}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500 uppercase">
                        {item.tipo}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-gray-700 whitespace-pre-line">
                      {item.descripcion}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseTracker;
